import { Component, computed, inject, isDevMode, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { HtaNavigationItem } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { ImpersonationBannerComponent } from 'app/layout/common/impersonation-banner/impersonation-banner.component';
import { TenantBadgeComponent } from 'app/layout/common/tenant-badge/tenant-badge.component';
import { TenantSwitcherComponent } from 'app/layout/common/tenant-switcher/tenant-switcher.component';
import { Subject, takeUntil } from 'rxjs';
import { AuthService, User, TenantService, EmpresaService } from '@hta/auth';
import { SucursalContextService } from 'app/core/sucursal/sucursal-context.service';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
@Component({
    selector: 'futuristic-layout',
    templateUrl: './futuristic.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        UserComponent,
        MatButtonModule,
        MatIconModule,
        RouterLink,
        RouterLinkActive,
        RouterOutlet,
        ImpersonationBannerComponent,
        TenantBadgeComponent,
        TenantSwitcherComponent,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
    ],
})
export class FuturisticLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall = false;
    user!: User;
    sidebarOpen = signal(true);

    readonly tenantService   = inject(TenantService);
    readonly empresaService  = inject(EmpresaService);
    readonly sucursalContext = inject(SucursalContextService);
    readonly isDev = isDevMode();

    devPanelExpanded = false;

    private readonly _router = inject(Router);
    private readonly _navigationService = inject(NavigationService);
    private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);
    private readonly authService = inject(AuthService);
    private _unsubscribeAll = new Subject<void>();

    readonly modules = signal<HtaNavigationItem[]>([]);
    readonly selectedModuleId = signal<string | null>(null);
    readonly selectedModule = computed(() =>
        this.modules().find(module => module.id === this.selectedModuleId()) ?? null
    );
    readonly selectedMenuItems = computed(() =>
        this._flattenNavigation(this.selectedModule()?.children ?? [])
    );
    readonly navLoading = this._navigationService.loading;

    ngOnInit(): void {
        this.sucursalContext.init();

        this._navigationService.get().subscribe({
            next: (navigation) => {
                // El observable puede emitir mientras el layout está siendo comprobado.
                // Crear las vistas del @for en ese mismo ciclo provoca NG0100.
                setTimeout(() => {
                    this.modules.set(navigation.futuristic ?? []);
                    this._syncSelectedModule();
                }, 0);
            }
        });

        this._router.events
            .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Postergar la mutación de signals al siguiente macrotask para evitar
                // ExpressionChangedAfterItHasBeenCheckedError (NG0100) en zoneless:
                // si mutamos expandedModuleId/sidebarOpen durante el CD cycle del padre,
                // Angular aborta el render de los componentes hijo.
                setTimeout(() => {
                    if (!this.sucursalContext.currentSucursal()) {
                        this.sucursalContext.reload();
                    }
                    if (this.isScreenSmall) {
                        this.sidebarOpen.set(false);
                    }
                    this._syncSelectedModule();
                }, 0);
            });

        this.authService.getUser()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user) => {
                this.user = user!;
            });

        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                const wasSmall = this.isScreenSmall;
                this.isScreenSmall = !matchingAliases.includes('md');
                if (wasSmall !== this.isScreenSmall) {
                    this.sidebarOpen.set(!this.isScreenSmall);
                }
            });

        setTimeout(() => this._syncSelectedModule(), 500);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    selectModule(moduleId: string): void {
        if (this.modules().some(module => module.id === moduleId)) {
            this.selectedModuleId.set(moduleId);
        }
    }

    toggleNavigation(): void {
        this.sidebarOpen.update(v => !v);
    }

    hasActiveChild(item: HtaNavigationItem): boolean {
        const url = this._router.url;
        if (item.link && url.startsWith(item.link)) return true;
        return item.children?.some(c => this.hasActiveChild(c)) ?? false;
    }

    private _syncSelectedModule(): void {
        const active = this.modules().find(m => this.hasActiveChild(m));
        if (active?.id) {
            this.selectedModuleId.set(active.id);
            return;
        }

        const selectionIsAvailable = this.modules().some(
            module => module.id === this.selectedModuleId()
        );
        if (!selectionIsAvailable) {
            this.selectedModuleId.set(this.modules()[0]?.id ?? null);
        }
    }

    private _flattenNavigation(items: HtaNavigationItem[]): HtaNavigationItem[] {
        return items.flatMap(item =>
            item.link ? [item] : this._flattenNavigation(item.children ?? [])
        );
    }
}
